import { useMemo, useState } from 'react';
import { parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import ReservationDetailModal from './ReservationDetailModal';

const DailyScheduleView = ({ schedules, selectedDate, onReservationUpdate }) => {
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedBoard, setSelectedBoard] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const handleSlotClick = (slot, board) => {
    if (slot.reservationId && !slot.isBlocked) {
      setSelectedSlot(slot);
      setSelectedBoard(board);
      setShowModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedSlot(null);
    setSelectedBoard(null);
  };

  const handleCancelReservation = async (reservationId, note, sendEmail) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/reservations/${reservationId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ note, sendEmail })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to cancel reservation');
      }

      // Trigger refresh of schedule data
      if (onReservationUpdate) {
        onReservationUpdate();
      }
    } catch (error) {
      console.error('Error cancelling reservation:', error);
      throw error;
    }
  };

  const handleSendMessage = async (userId, message) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/users/${userId}/message`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ message })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      alert('Message sent successfully!');
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message. Please try again.');
      throw error;
    }
  };
  // Generate a list of all unique time slots across all boards
  const timeSlots = useMemo(() => {
    const allSlots = new Set();
    schedules.forEach(board => {
      board.timeSlots.forEach(slot => {
        const startTime = parseISO(slot.startTime);
        const timeKey = formatInTimeZone(startTime, board.gymTimezone || 'America/Los_Angeles', 'HH:mm');
        allSlots.add(timeKey);
      });
    });
    return Array.from(allSlots).sort();
  }, [schedules]);

  const getBoardTypeColor = (type) => {
    const colors = {
      tension: 'bg-purple-600',
      moon: 'bg-blue-600',
      kilter: 'bg-green-600',
      grasshopper: 'bg-yellow-600',
      other: 'bg-gray-600'
    };
    return colors[type?.toLowerCase()] || colors.other;
  };

  const getReservationColor = (slot) => {
    if (slot.isBlocked) {
      return 'bg-orange-500';
    }
    if (slot.reservationId) {
      if (slot.reservationStatus === 'checked_in') {
        return 'bg-green-500';
      }
      return 'bg-blue-500';
    }
    return 'bg-gray-100';
  };

  const getReservationBorderColor = (slot) => {
    if (slot.isBlocked) {
      return 'border-orange-600';
    }
    if (slot.reservationId) {
      if (slot.reservationStatus === 'checked_in') {
        return 'border-green-600';
      }
      return 'border-blue-600';
    }
    return 'border-gray-300';
  };

  const getReservationTextColor = (slot) => {
    if (slot.isBlocked || slot.reservationId) {
      return 'text-white';
    }
    return 'text-gray-400';
  };

  const getSlotForTime = (board, timeKey) => {
    return board.timeSlots.find(slot => {
      const startTime = parseISO(slot.startTime);
      const slotTimeKey = formatInTimeZone(startTime, board.gymTimezone || 'America/Los_Angeles', 'HH:mm');
      return slotTimeKey === timeKey;
    });
  };

  const formatTime = (timeKey) => {
    const [hours, minutes] = timeKey.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getReservationText = (slot) => {
    if (slot.isBlocked) {
      return slot.blockedReason || 'Blocked';
    }
    if (slot.reservationId) {
      const name = `${slot.reservedByFirstName} ${slot.reservedByLastName}`;
      if (slot.reservationStatus === 'checked_in') {
        return `${name} ✓`;
      }
      return name;
    }
    return 'Available';
  };

  return (
    <div className="space-y-6">
      {/* Calendar Grid */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div>
          <div className="min-w-full">
            {/* Header Row - Board Names */}
            <div
              className="grid border-b-2 border-gray-300 sticky top-0 bg-white z-10"
              style={{ gridTemplateColumns: `96px repeat(${schedules.length}, 1fr)` }}
            >
              {/* Time Column Header */}
              <div className="p-4 font-semibold text-gray-700 border-r-2 border-gray-300">
                Time
              </div>

              {/* Board Headers */}
              {schedules.map((board) => (
                <div
                  key={board.boardId}
                  className="p-4 border-r border-gray-200 min-w-0"
                >
                  <div className="flex flex-col gap-2">
                    <div className="font-bold text-gray-900 truncate">
                      {board.boardName}
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <span className={`px-2 py-1 rounded text-xs font-medium text-white ${getBoardTypeColor(board.boardType)}`}>
                        {board.boardType}
                      </span>
                      {board.angle && (
                        <span className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs font-medium">
                          {board.angle}°
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Time Slot Rows */}
            <div className="max-h-[600px] overflow-y-auto">
              {timeSlots.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <p>No time slots scheduled for this day.</p>
                  <p className="text-sm mt-1">Time slots will be generated when users make reservations.</p>
                </div>
              ) : (
                timeSlots.map((timeKey) => (
                  <div
                    key={timeKey}
                    className="grid border-b border-gray-200 hover:bg-gray-50"
                    style={{ gridTemplateColumns: `96px repeat(${schedules.length}, 1fr)` }}
                  >
                    {/* Time Label */}
                    <div className="p-4 font-medium text-gray-600 border-r-2 border-gray-300 text-sm">
                      {formatTime(timeKey)}
                    </div>

                    {/* Board Cells */}
                    {schedules.map((board) => {
                      const slot = getSlotForTime(board, timeKey);

                      return (
                        <div
                          key={`${board.boardId}-${timeKey}`}
                          className="p-2 border-r border-gray-200 min-w-0"
                        >
                          {slot ? (
                            <div
                              className={`
                                h-full min-h-[60px] rounded-lg p-3 border-2
                                ${getReservationColor(slot)}
                                ${getReservationBorderColor(slot)}
                                ${getReservationTextColor(slot)}
                                transition-all hover:shadow-md
                                ${slot.reservationId && !slot.isBlocked ? 'cursor-pointer' : 'cursor-default'}
                              `}
                              title={getReservationText(slot)}
                              onClick={() => handleSlotClick(slot, board)}
                            >
                              <div className="text-xs font-semibold truncate">
                                {getReservationText(slot)}
                              </div>
                              {slot.checkedInAt && (
                                <div className="text-xs opacity-90 mt-1">
                                  @ {formatInTimeZone(parseISO(slot.checkedInAt), board.gymTimezone || 'America/Los_Angeles', 'h:mm a')}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="h-full min-h-[60px]"></div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-bold mb-4 text-gray-900">Legend</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-gray-100 border-2 border-gray-300 rounded"></div>
            <span className="text-sm text-gray-700">Available</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-blue-500 border-2 border-blue-600 rounded"></div>
            <span className="text-sm text-gray-700">Reserved</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-green-500 border-2 border-green-600 rounded"></div>
            <span className="text-sm text-gray-700">Checked In</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-orange-500 border-2 border-orange-600 rounded"></div>
            <span className="text-sm text-gray-700">Blocked</span>
          </div>
        </div>
      </div>

      {/* Overall Summary */}
      <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-lg shadow-md p-6 text-white">
        <h3 className="text-lg font-bold mb-4">Daily Summary</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-white/10 rounded-lg p-4">
            <div className="text-2xl font-bold">
              {schedules.length}
            </div>
            <div className="text-sm text-gray-300">Total Boards</div>
          </div>
          <div className="bg-white/10 rounded-lg p-4">
            <div className="text-2xl font-bold">
              {schedules.reduce((sum, board) => sum + board.timeSlots.length, 0)}
            </div>
            <div className="text-sm text-gray-300">Total Time Slots</div>
          </div>
          <div className="bg-white/10 rounded-lg p-4">
            <div className="text-2xl font-bold text-blue-300">
              {schedules.reduce((sum, board) =>
                sum + board.timeSlots.filter(s => s.reservationId && !s.isBlocked).length, 0
              )}
            </div>
            <div className="text-sm text-gray-300">Total Reservations</div>
          </div>
          <div className="bg-white/10 rounded-lg p-4">
            <div className="text-2xl font-bold text-green-300">
              {schedules.reduce((sum, board) =>
                sum + board.timeSlots.filter(s => s.reservationStatus === 'checked_in').length, 0
              )}
            </div>
            <div className="text-sm text-gray-300">Checked In</div>
          </div>
        </div>
      </div>

      {/* Reservation Detail Modal */}
      <ReservationDetailModal
        isOpen={showModal}
        onClose={handleCloseModal}
        slot={selectedSlot}
        board={selectedBoard}
        onCancelReservation={handleCancelReservation}
        onSendMessage={handleSendMessage}
      />
    </div>
  );
};

export default DailyScheduleView;
